import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Rating,
  Button,
  CircularProgress,
} from "@mui/material";
import { useChatProducts } from "../context/ChatProductsContext";
import ProductDetailView from "./ProductDetailView";
import { getUserProfile } from "../utils/auth";

export default function ProductRecommendationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatProducts } = useChatProducts();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(() => {
    // Get products from context, location state, or sessionStorage
    // Check location.state first (highest priority) - includes timestamp to force updates
    if (location.state?.products && location.state.products.length > 0) {
      setProducts(location.state.products);
      // Also update sessionStorage for consistency
      sessionStorage.setItem(
        "orbiee_recommendation_products",
        JSON.stringify(location.state.products)
      );
    } else if (chatProducts && chatProducts.length > 0) {
      setProducts(chatProducts);
    } else {
      // Check sessionStorage for products from OrbieeFlow
      const storedProducts = sessionStorage.getItem("orbiee_recommendation_products");
      if (storedProducts) {
        try {
          const parsedProducts = JSON.parse(storedProducts);
          if (Array.isArray(parsedProducts) && parsedProducts.length > 0) {
            setProducts(parsedProducts);
            // Don't clear sessionStorage - keep it for potential re-renders
          }
        } catch (e) {
          console.warn("Failed to parse stored products", e);
          sessionStorage.removeItem("orbiee_recommendation_products");
        }
      }
    }
  }, [location.state, chatProducts]);

  useEffect(() => {
    setLoading(true);
    loadProducts();
    // Resolve loading after we've read from state/context/sessionStorage
    setLoading(false);
  }, [loadProducts, location.key]);

  // Listen for custom event to update products when already on the page
  useEffect(() => {
    const handleProductsUpdate = (event) => {
      const { products: newProducts } = event.detail || {};
      if (newProducts && Array.isArray(newProducts) && newProducts.length > 0) {
        setProducts(newProducts);
        // Update sessionStorage
        sessionStorage.setItem(
          "orbiee_recommendation_products",
          JSON.stringify(newProducts)
        );
      }
    };

    window.addEventListener("orbiee:productsUpdated", handleProductsUpdate);
    return () => {
      window.removeEventListener("orbiee:productsUpdated", handleProductsUpdate);
    };
  }, []);

  const handleProductClick = (product) => {
    if (!product?.id) return;

    // Let ChatBot know which product was clicked so it can prefill/send the name
    try {
      window.dispatchEvent(
        new CustomEvent("chat:productSelected", {
          detail: { product },
        })
      );
    } catch (e) {
      console.warn("Failed to notify chat of product click", e);
    }

    navigate(`/item/${product.id}`);
  };

  const getImageUrl = (rawUrl) => {
    if (!rawUrl) return null;
    return rawUrl
      .trim()
      .replace("storage.cloud.google.com", "storage.googleapis.com");
  };

  const formatPrice = (price, currency = "USD") => {
    const numericPrice = parseFloat(price);
    if (Number.isNaN(numericPrice)) return "—";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(numericPrice);
    } catch (e) {
      // Fallback if currency code is invalid
      return `${currency} ${numericPrice.toFixed(2)}`;
    }
  };

  const getSizes = (product) => {
    // Try to get sizes from various possible fields
    if (product.attributes?.size) {
      const sizeStr = Array.isArray(product.attributes.size)
        ? product.attributes.size.join(" ")
        : String(product.attributes.size);
      return sizeStr.split(/[,\s]+/).filter(Boolean);
    }
    if (product.size) {
      return Array.isArray(product.size) ? product.size : [product.size];
    }
    // Default sizes
    return ["XS", "S", "M", "L"];
  };

  const getRating = (product) => {
    
    return product.rating || product.rating_score || 4.5;
  };

  const getReviewCount = (product) => {
    return product.review_count || product.reviews || Math.floor(Math.random() * 200) + 50;
  };

  const isOnSale = (product) => {
    return (
      product.original_price &&
      product.price &&
      parseFloat(product.original_price) > parseFloat(product.price)
    );
  };

  const hasLabel = (product) => {
    return product.is_bestseller || isOnSale(product);
  };

  // Show loader until we've resolved products from state/context/storage
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
          Loading recommendations...
        </Typography>
      </Container>
    );
  }

  // No data or failed: show message only after loading is done
  if (products.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          No Products Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          We couldn't find any product recommendations at the moment.
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}>
          Go Back to Home
        </Button>
      </Container>
    );
  }

  if (products.length === 1) {
  const singleProduct = products[0];
  
  const mockDetail = {
    product: {
      id: singleProduct.id,
      title: singleProduct.title || singleProduct.name,
      description: singleProduct.description || "",
      images: [
        singleProduct.images?.[0] || 
        singleProduct.image_url || 
        singleProduct.image || 
        null
      ].filter(Boolean),
      price_info: {
        price: singleProduct.price_info?.price || singleProduct.price || 0,
        originalPrice: singleProduct.price_info?.original_price || singleProduct.original_price || null,
        currencyCode: singleProduct.price_info?.currencyCode || singleProduct.price_info?.currency || singleProduct.currency || "USD",
      },
      attributes: {
        size: singleProduct.attributes?.size || singleProduct.size,
        color: singleProduct.attributes?.color || "N/A",
        fabric: singleProduct.attributes?.fabric,
        fit: singleProduct.attributes?.fit,
        rating: singleProduct.rating || 4.5,
        review_count: singleProduct.review_count || 100,
      },
      categories: singleProduct.categories || [],
    },
    variants: singleProduct.variants || [],
  };

  const userProfile = getUserProfile();
  const customerId = userProfile?.customer_id;


  return (
    <ProductDetailView
      productDetail={mockDetail}
      customerId={customerId}
      onBack={() => navigate(-1)} // or pass navigate
    />
  );
}

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
          }}
        >
          Product Recommendations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Based on your preferences
        </Typography>
      </Box>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {products.map((product, index) => {
          const imageUrl = getImageUrl(
            product.image ||
              product.image_url ||
              product.images?.[0] ||
              product.product?.image
          );
          const title =
            product.enhanced_name ||
            product.title ||
            product.product?.title ||
            product.name ||
            "Product";
          const currency =
            product.currency ||
            product.price_info?.currencyCode ||
            product.price_info?.currency ||
            product.product?.currency ||
            "USD";
          const price = parseFloat(product.price || product.product?.price || 0);
          const originalPrice = parseFloat(
            product.original_price ||
              product.product?.original_price ||
              product.price_info?.originalPrice ||
              0
          );
          const sizes = getSizes(product);
          const rating = getRating(product);
          const reviewCount = getReviewCount(product);
          const onSale = isOnSale(product);
          const isBestseller = product.is_bestseller || product.bestseller;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id || index}>
              <Card
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                  },
                }}
                onClick={() => handleProductClick(product)}
              >
                {/* Product Image */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "350px",
                    overflow: "hidden",
                    bgcolor: "grey.100",
                  }}
                >
                  {imageUrl ? (
                    <CardMedia
                      component="img"
                      image={imageUrl}
                      alt={title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${product.id || index}/400/500`;
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.200",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No Image
                      </Typography>
                    </Box>
                  )}

                  {/* Bestseller/Sale Label */}
                  {hasLabel(product) && (
                    <Chip
                      label={isBestseller ? "Bestseller" : "Sale"}
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        bgcolor: isBestseller ? "primary.main" : "error.main",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: "24px",
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>

                {/* Product Info */}
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  {/* Product Title */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.5,
                      minHeight: "40px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {title}
                  </Typography>

                  {/* Product ID */}
                  {product.id && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        mb: 1,
                        display: "block",
                        fontFamily: "monospace",
                      }}
                    >
                      ID: {product.id}
                    </Typography>
                  )}

                  {/* Rating */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1.5,
                    }}
                  >
                    <Rating
                      value={rating}
                      precision={0.1}
                      readOnly
                      size="small"
                      sx={{
                        "& .MuiRating-iconFilled": {
                          color: "warning.main",
                        },
                        fontSize: "0.9rem",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {rating.toFixed(1)}/{reviewCount}
                    </Typography>
                  </Box>

                  {/* Price */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: "1.1rem",
                      }}
                    >
                      {formatPrice(price, currency)}
                    </Typography>
                    {onSale && originalPrice > price && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textDecoration: "line-through",
                          fontSize: "0.9rem",
                        }}
                      >
                        {formatPrice(originalPrice, currency)}
                      </Typography>
                    )}
                  </Box>

                  {/* Sizes */}
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Available Sizes
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {sizes.map((size, idx) => (
                        <Chip
                          key={idx}
                          label={size}
                          size="small"
                          sx={{
                            height: "24px",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            bgcolor: "grey.100",
                            color: "text.primary",
                            border: "1px solid",
                            borderColor: "grey.300",
                            "&:hover": {
                              bgcolor: "grey.200",
                              borderColor: "grey.400",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
}

